import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, FileText, Building2, X, CheckCircle2, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: "'Satoshi', sans-serif" };

/**
 * One-time celebratory welcome modal — fires after first login when:
 *  - user.trial_active = true
 *  - user.trial_celebrated = false (server-side flag persisted via /subscription/trial-celebrated)
 * Dismissal updates both server state and a local fallback to prevent re-show across reloads.
 */
export default function TrialWelcomeModal() {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const localKey = `is_trial_celebrated_${user.user_id || user.id || user.email}`;
    const localFlag = typeof window !== "undefined" && window.localStorage.getItem(localKey);
    if (user.trial_active && !user.trial_celebrated && !localFlag) {
      // Defer one tick to let the page render first
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated, user]);

  const dismiss = async () => {
    setOpen(false);
    try {
      await axios.post(`${API}/subscription/trial-celebrated`, {}, { withCredentials: true });
    } catch { /* non-fatal */ }
    try {
      const key = `is_trial_celebrated_${user?.user_id || user?.id || user?.email}`;
      window.localStorage.setItem(key, "1");
    } catch { /* non-fatal */ }
    if (refreshUser) refreshUser();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="trial-welcome-modal"
          onClick={dismiss}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.45 }}
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-violet-600 via-blue-600 to-emerald-500 px-6 py-8 text-center relative">
              <button onClick={dismiss} className="absolute top-3 right-3 text-white/80 hover:text-white p-1" aria-label="Close" data-testid="trial-modal-close">
                <X className="w-5 h-5" />
              </button>
              <motion.div
                animate={{ rotate: [0, 8, -8, 0] }}
                transition={{ repeat: 2, duration: 0.5 }}
                className="inline-flex bg-white/20 backdrop-blur rounded-2xl p-3 mb-3"
              >
                <Sparkles className="w-7 h-7 text-white" />
              </motion.div>
              <h2 className="text-2xl sm:text-3xl font-black text-white" style={heading} data-testid="trial-modal-title">
                Welcome to InvoiceSetu!
              </h2>
              <p className="text-sm text-white/90 mt-2 max-w-sm mx-auto">
                You just unlocked <span className="font-bold">1 month of free Growth access</span>. Send unlimited invoices, run GST reports, collaborate with your CA — all on us.
              </p>
              {typeof user?.trial_days_left === "number" && (
                <div className="inline-flex items-center gap-1.5 mt-4 bg-white/20 backdrop-blur rounded-full px-3 py-1 text-white text-xs font-semibold">
                  <Clock className="w-3.5 h-3.5" /> {user.trial_days_left} days left
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="space-y-2 mb-5">
                {[
                  "Unlimited GST invoices, quotations & credit notes",
                  "GSTR-1 and 3B export, HSN summary",
                  "Add your CA — collaborate in real time",
                  "Remove InvoiceSetu branding from PDFs",
                ].map((feat) => (
                  <div key={feat} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Link
                  to="/create"
                  onClick={dismiss}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors"
                  data-testid="trial-modal-cta-invoice"
                >
                  <FileText className="w-4 h-4" /> Start Creating Invoice
                </Link>
                <Link
                  to="/company/setup"
                  onClick={dismiss}
                  className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-900 font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors"
                  data-testid="trial-modal-cta-company"
                >
                  <Building2 className="w-4 h-4" /> Complete Company Profile
                </Link>
              </div>
              <button onClick={dismiss} className="block mx-auto mt-4 text-xs text-slate-400 hover:text-slate-600" data-testid="trial-modal-explore">
                I'll explore on my own &rarr;
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
